import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Grid,
  Typography,
  Box,
  useTheme,
  useMediaQuery,
  IconButton,
  Container,
  darken,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import Image from "../components/common/Image";
import { useTranslation } from "react-i18next";
import * as Constant from "../features/constant";

const Language = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const mobileView = useMediaQuery(theme.breakpoints.down("sm"));

  // ✅ Auto-reload or navigate when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      localStorage.setItem("language", i18n.language);

      if (process.env.REACT_APP_LANG_PATH_PARAM) {
        navigate(Constant.getPath("/"));
      } else {
        window.location.reload();
      }
    };

    i18n.on("languageChanged", handleLanguageChange);
    return () => i18n.off("languageChanged", handleLanguageChange);
  }, [i18n, navigate]);

  const countryCode = process.env.REACT_APP_COUNTRY?.toUpperCase();
  const countryInfo = Constant.COUNTRY_LANGUAGE_INFO[countryCode]; 

  if (!countryInfo) return null;

  return (
    <>
      {mobileView && (
        <>
          {/* 🔹 Header bar */}
          <Grid
            container
            py={1}
            backgroundColor={darken(theme.palette.background.paper, 0.4)}
          >
            <Grid item xs={4}>
              <IconButton onClick={() => navigate(-1)}>
                <ArrowBack sx={{ color: process.env.REACT_APP_COLOR_TEXT }} />
              </IconButton>
            </Grid>
            <Grid item xs={4} alignSelf="center">
              <Typography
                textAlign="center"
                fontWeight={600}
                color={theme.palette.primary.main}
              >
                {t("language")}
              </Typography>
            </Grid>
          </Grid>

          {/* 🔹 Language selector */}
          <Container
            sx={{
              minHeight: "100vh",
              backgroundColor: "black",
            }}
          >
            <Box display="flex" flexDirection="column" px={2} pt={5}>
              <Box
                display="flex"
                justifyContent="flex-start"
                alignItems="center"
                pt={1}
              >
                {/* Flag */}
                <Image
                  width="45px"
                  height="45px"
                  src={countryInfo.flag}
                  alt={countryInfo.name}
                />

                {/* Country name + languages */}
                <Box display="flex" flexDirection="column" pl={1}>
                  <Typography
                    fontWeight={600}
                    sx={{
                      color: process.env.REACT_APP_COLOR_TEXT,
                      lineHeight: 1.2,
                    }}
                  >
                    {countryInfo.name}
                  </Typography>

                  <Box display="flex" alignItems="center">
                    {countryInfo.languages.map((language, index) => (
                      <React.Fragment key={language.code}>
                        <Typography
                          component="span"
                          sx={{
                            cursor: "pointer",
                            color:
                              i18n.language.toUpperCase() === language.code
                                ? theme.palette.primary.main
                                : process.env.REACT_APP_COLOR_TEXT,
                            "&:hover": { textDecoration: "underline" },
                          }}
                          onClick={() => {
                            const newLang = language.code?.toLowerCase();

                            localStorage.setItem("language", newLang);
                            i18n.changeLanguage(newLang);

                            // Delay a bit to ensure i18n finishes updating before reload
                            setTimeout(() => {
                              window.location.reload();
                            }, 150);
                          }}
                        >
                          {language.label}
                        </Typography>

                        {index <
                          countryInfo.languages.length - 1 && (
                          <Typography
                            component="span"
                            px={1}
                            sx={{
                              color: process.env.REACT_APP_COLOR_TEXT,
                            }}
                          >
                            |
                          </Typography>
                        )}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Container>
        </>
      )}
    </>
  );
};

export default Language;
